import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Shield, User, Wrench, CreditCard, Edit2, X, UserMinus, UserCheck } from 'lucide-react';
import { useAppContext, Staff } from '../../context/AppContext';
const roleOptions: Staff['role'][] = ['admin', 'support', 'billing', 'technician'];
const permissionOptions = [{
  id: 'tickets_read',
  label: 'View Tickets'
}, {
  id: 'tickets_write',
  label: 'Manage Tickets'
}, {
  id: 'invoices_read',
  label: 'View Invoices'
}, {
  id: 'invoices_write',
  label: 'Manage Invoices'
}, {
  id: 'packages_read',
  label: 'View Packages'
}, {
  id: 'packages_write',
  label: 'Manage Packages'
}, {
  id: 'staff_manage',
  label: 'Manage Staff'
}, {
  id: 'servers_manage',
  label: 'Manage Servers'
}, {
  id: 'theme_manage',
  label: 'Manage Theme'
}, {
  id: 'all',
  label: 'Full Admin Access'
}];
export function StaffManager() {
  const {
    staff,
    addStaff,
    updateStaff,
    deleteStaff
  } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<Staff | null>(null);
  const [selectedPerms, setSelectedPerms] = useState<string[]>([]);
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Shield size={16} className="text-red-500" data-id="element-762" />;
      case 'support':
        return <User size={16} className="text-blue-500" data-id="element-763" />;
      case 'technician':
        return <Wrench size={16} className="text-purple-500" data-id="element-764" />;
      case 'billing':
        return <CreditCard size={16} className="text-emerald-500" data-id="element-765" />;
      default:
        return <User size={16} data-id="element-766" />;
    }
  };
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'support':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'technician':
        return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      case 'billing':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      default:
        return 'bg-slate-800 text-slate-400 border-slate-700';
    }
  };
  const openAdd = () => {
    setEditingMember(null);
    setSelectedPerms([]);
    setIsModalOpen(true);
  };
  const openEdit = (member: Staff) => {
    setEditingMember(member);
    setSelectedPerms([...member.permissions]);
    setIsModalOpen(true);
  };
  const togglePerm = (perm: string) => {
    if (perm === 'all') {
      setSelectedPerms(selectedPerms.includes('all') ? [] : ['all']);
      return;
    }
    const filtered = selectedPerms.filter(p => p !== 'all');
    if (filtered.includes(perm)) {
      setSelectedPerms(filtered.filter(p => p !== perm));
    } else {
      setSelectedPerms([...filtered, perm]);
    }
  };
  const handleToggleStatus = (member: Staff) => {
    updateStaff(member.id, {
      status: member.status === 'active' ? 'inactive' : 'active'
    });
  };
  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const data: Staff = {
      id: editingMember ? editingMember.id : `s${Date.now()}`,
      name: form.get('name') as string,
      email: form.get('email') as string,
      role: form.get('role') as Staff['role'],
      status: editingMember ? editingMember.status : 'active',
      permissions: selectedPerms,
      createdAt: editingMember ? editingMember.createdAt : new Date().toISOString().split('T')[0]
    };
    if (editingMember) {
      updateStaff(data.id, data);
    } else {
      addStaff(data);
    }
    setIsModalOpen(false);
    setEditingMember(null);
  };
  return <div className="space-y-8" data-id="element-767">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-768">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-769">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-770">
            Staff Management
          </h1>
          <p className="text-slate-400" data-id="element-771">
            Manage team members and their permissions.
          </p>
        </motion.div>
        <button onClick={openAdd} className="bg-fox-600 hover:bg-fox-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors" data-id="element-772">
          <Plus size={18} data-id="element-773" /> Add Staff Member
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-774">
        {staff.map((member, i) => <motion.div key={member.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} className={`bg-slate-950 border rounded-2xl p-6 relative ${member.status === 'inactive' ? 'border-slate-800 opacity-60' : 'border-slate-800'}`} data-id="element-775">
            <div className="absolute top-6 right-6 flex items-center gap-2" data-id="element-776">
              <span className={`w-2.5 h-2.5 rounded-full inline-block ${member.status === 'active' ? 'bg-emerald-500' : 'bg-slate-600'}`} data-id="element-777" />
              <span className="text-xs text-slate-500" data-id="element-778">{member.status}</span>
            </div>

            <div className="flex items-center gap-4 mb-4" data-id="element-779">
              <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center text-xl font-bold text-slate-300 border border-slate-700" data-id="element-780">
                {member.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div data-id="element-781">
                <h3 className="text-lg font-bold text-white" data-id="element-782">{member.name}</h3>
                <p className="text-slate-400 text-sm" data-id="element-783">{member.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-2" data-id="element-784">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium ${getRoleColor(member.role)}`} data-id="element-785">
                {getRoleIcon(member.role)}
                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
              </span>
            </div>

            <div className="flex flex-wrap gap-1 mb-5" data-id="element-786">
              {member.permissions.slice(0, 3).map(p => <span key={p} className="text-[10px] px-2 py-0.5 bg-slate-900 border border-slate-800 rounded text-slate-400" data-id="element-787">
                  {p === 'all' ? 'Full Access' : p.replace('_', ' ')}
                </span>)}
              {member.permissions.length > 3 && <span className="text-[10px] px-2 py-0.5 bg-slate-900 border border-slate-800 rounded text-slate-400" data-id="element-788">
                  +{member.permissions.length - 3} more
                </span>}
            </div>

            <div className="flex gap-2" data-id="element-789">
              <button onClick={() => openEdit(member)} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2" data-id="element-790">
                <Edit2 size={14} data-id="element-791" /> Edit
              </button>
              <button onClick={() => handleToggleStatus(member)} className={`px-3 rounded-lg transition-colors flex items-center justify-center ${member.status === 'active' ? 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-500' : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500'}`} title={member.status === 'active' ? 'Deactivate' : 'Activate'} data-id="element-792">
                {member.status === 'active' ? <UserMinus size={16} data-id="element-793" /> : <UserCheck size={16} data-id="element-794" />}
              </button>
            </div>
          </motion.div>)}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence data-id="element-795">
        {isModalOpen && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-796">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-id="element-797">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10" data-id="element-798">
                <h2 className="text-xl font-bold text-white" data-id="element-799">
                  {editingMember ? 'Edit Staff Member' : 'Add Staff Member'}
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white" data-id="element-800">
                  <X size={24} data-id="element-801" />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-6" data-id="element-802">
                <div className="space-y-2" data-id="element-803">
                  <label className="text-sm font-medium text-slate-300" data-id="element-804">
                    Full Name *
                  </label>
                  <input name="name" required defaultValue={editingMember?.name} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="John Doe" data-id="element-805" />
                </div>

                <div className="space-y-2" data-id="element-806">
                  <label className="text-sm font-medium text-slate-300" data-id="element-807">
                    Email Address *
                  </label>
                  <input name="email" type="email" required defaultValue={editingMember?.email} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="john@foxlinehosting.com" data-id="element-808" />
                </div>

                <div className="space-y-2" data-id="element-809">
                  <label className="text-sm font-medium text-slate-300" data-id="element-810">
                    Role *
                  </label>
                  <select name="role" defaultValue={editingMember?.role || 'support'} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-811">
                    {roleOptions.map(r => <option key={r} value={r} data-id="element-812">
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </option>)}
                  </select>
                </div>

                <div className="space-y-3" data-id="element-813">
                  <label className="text-sm font-medium text-slate-300" data-id="element-814">
                    Permissions
                  </label>
                  <div className="grid grid-cols-2 gap-2" data-id="element-815">
                    {permissionOptions.map(perm => <label key={perm.id} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${selectedPerms.includes(perm.id) ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`} data-id="element-816">
                        <input type="checkbox" checked={selectedPerms.includes(perm.id)} onChange={() => togglePerm(perm.id)} className="w-4 h-4 rounded border-slate-700 bg-slate-950 text-fox-600 focus:ring-fox-500" data-id="element-817" />
                        <span className="text-sm text-slate-300" data-id="element-818">
                          {perm.label}
                        </span>
                      </label>)}
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-800 flex justify-end gap-3" data-id="element-819">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white transition-colors" data-id="element-820">
                    Cancel
                  </button>
                  <button type="submit" className="bg-fox-600 hover:bg-fox-500 text-white px-6 py-2 rounded-lg font-medium transition-colors" data-id="element-821">
                    {editingMember ? 'Save Changes' : 'Add Member'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}